import { AfterViewInit, Component, computed, DestroyRef, ElementRef, inject, signal, ViewChild } from '@angular/core';
import { WheelAction, WheelSegment, WheelBonusReward, WheelBonus, WheelProductReward } from './models/wheel-of-fortune.model';
import { WheelOfFortuneService } from './services/wheel-of-fortune.service';
import { CommonModule } from '@angular/common';
import { catchError, forkJoin, of, take, tap, timer } from 'rxjs';
import { WheelButtonComponent } from "./components/wheel-button/wheel-button.component";
import { WheelTrophyComponent } from "./components/wheel-trophy/wheel-trophy.component";
import { WheelBonusCardComponent } from './components/wheel-bonus-card/wheel-bonus-card.component';
import { WheelDynamicTextComponent } from "./components/wheel-dynamic-text/wheel-dynamic-text.component";
import { WheelOfFortuneApiService } from './services/wheel-of-fortune-api.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { ToastService } from '../utils/services/toast.service';
import { ToastsContainer } from "../../toastes";
import { BreakpointObserver, LayoutModule, Breakpoints } from '@angular/cdk/layout';

@Component({
  selector: 'app-wheel-of-fortune',
  standalone: true,
  imports: [CommonModule, WheelButtonComponent, WheelTrophyComponent, WheelBonusCardComponent, WheelDynamicTextComponent, ToastsContainer, LayoutModule],
  templateUrl: './wheel-of-fortune.component.html',
  styleUrl: './wheel-of-fortune.component.scss'
})
export class WheelOfFortuneComponent implements  AfterViewInit{
  @ViewChild('wheelCanvas', { static: false }) canvasRef!: ElementRef;
  @ViewChild('wheel', { static: false }) wheel!: ElementRef<HTMLImageElement>;
  @ViewChild('errorRef', { static: true }) errorRef!: ElementRef;

  private imageCache = new Map<string, HTMLImageElement>();

  private readonly wheelService = inject(WheelOfFortuneService);

  private readonly wheelApiService = inject(WheelOfFortuneApiService);

  private readonly destroyRef = inject(DestroyRef);

  private readonly toastService = inject(ToastService);

  private bpObserver = inject(BreakpointObserver);

  private isSpinning = false;

  private ctx!: CanvasRenderingContext2D;

  private currentAngle = 0;

  protected readonly WHEEL_BUTTON_ACTION = WheelAction;

  public showTrophy = signal<boolean>(false);

  public showSpinMarket = this.wheelService.showSpinMarket;

  public wheelTrophy = computed(() => this.showTrophy() ? this.wheelService.currentWheelTrophy() : null);

  public showWheelGuy = computed(() => !this.showTrophy() && !this.showSpinMarket());

  public wheelSegments = signal<WheelSegment[]>([]);

  public isLoading = signal(false);

  public wheelHeader = this.wheelService.wheelHeader;
  public wheelDescription = this.wheelService.wheelDescription;
  public wheelSubtitle = this.wheelService.wheelSubtitle;
  public wheelButtons = this.wheelService.wheelButtons;

  public canSpin = signal<boolean>(false);

  public bonusMarketOptions = this.wheelService.wheelBonuses;

  public bonusBalance = signal<number>(0);

  public errorMessage = signal<string | null>(null);

  private canvasSize = signal<'s' | 'lg'>('s');

  private observeResize():void {
    this.bpObserver.observe(Breakpoints.Small)
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe({
      next:(isMobile)=>this.canvasSize.set(isMobile?'s':'lg')
    })
  }

  private wheelRequest() {
  return this.wheelApiService.getUserWheelState()
      .pipe(
        catchError(e => of(null)),
    )
  }

  private bonusesRequest() {
    return this.wheelApiService.getBonusBalance()
      .pipe(
        catchError(e => of(null)),
    )
  }

  ngAfterViewInit(): void{
    this.observeResize();
    this.initDefaultWheel();
  }

  private initDefaultWheel(): void {
    if (this.wheelService.isUserAuthorized()) {
      this.initAuthorizedWheel();
    } else {
      this.initUnauthorizedWheel();
    }
  }

  private initUnauthorizedWheel():void {
    this.wheelRequest()
      .subscribe({
        next: (response) => {
             if (response?.data) {
               const { rewards } = response.data;
            this.wheelService.setDefaultView(0, 0);

            if (rewards.length && !this.showTrophy() && this.canvasRef) {
              this.wheelSegments.set(this.mixBonusesAndProducts(rewards));
              this.drawWheel();
              this.startContinuousSpin(.1);
            }
          }
      }
    })
  }

  private initAuthorizedWheel():void {
    forkJoin([this.wheelRequest(), this.bonusesRequest()])
      .pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (([wheelResponse, bonusResponse]) => {
          if (wheelResponse?.data) {
            const { balance, rewards } = wheelResponse?.data;
            const bonuses = parseFloat(bonusResponse?.data?.balance || '0');
            //EDIT TO EMULATE VIEW WITH SPINS AND/OR BONUSES
            this.bonusBalance.set(bonuses);
            this.wheelService.setDefaultView(balance, bonuses);
            this.canSpin.set(Boolean(balance));

            if (rewards.length && !this.showTrophy() && this.canvasRef) {
              this.wheelSegments.set(this.mixBonusesAndProducts(rewards));
              this.drawWheel();
              this.startContinuousSpin(.1);
            }
          }
        })
      })
  }

  private mixBonusesAndProducts(segments: WheelSegment[]): WheelSegment[] {
  const counts: Record<'bonuses' | 'product'| 'spins', WheelSegment[]> = {
    bonuses: segments.filter(s => s.type === 'bonuses'),
    product: segments.filter(s => s.type === 'product'),
    spins: segments.filter(s => s.type === 'spins'),
    };

  const total = segments.length;
  const result: WheelSegment[] = [];
  let lastCategory: 'bonuses' | 'product'| 'spins' | null = null;

  for (let i = 0; i < total; i++) {
    const candidates = Object.entries(counts)
      .filter(([category, items]) => items.length > 0 && category !== lastCategory)
      .sort(([, aItems], [, bItems]) => bItems.length - aItems.length) as ['bonuses' | 'product'| 'spins', WheelSegment[]][];

    if (candidates.length === 0) {
      lastCategory = null;
      const availableCategories = Object.entries(counts)
        .filter(([, items]) => items.length > 0) as ['bonuses' | 'product'| 'spins', WheelSegment[]][];

      if (availableCategories.length === 0) {
        break;
      }

      const [chosenCategory, chosenItems] = availableCategories[0];
      result.push(chosenItems.pop()!);
      lastCategory = chosenCategory;
    } else {
      const [chosenCategory, chosenItems] = candidates[0];
      result.push(chosenItems.pop()!);
      lastCategory = chosenCategory;
    }
  }

  return result;
  }

  public wheelClickHandler(action: WheelAction) {
    this.wheelService.handleWheelAction(action);

    switch (action) {
      case WheelAction.NAVIGATE_TO_WHEEL: {
        this.initAuthorizedWheel();
        this.showTrophy.set(false);
        break;
      }

      case WheelAction.SPIN: {
        this.isLoading.set(true);
        this.spinWheel();
        break;
      }

      case WheelAction.BUY_SPINS: {
         this.wheelApiService.getBonusBalance()
          .pipe(
            take(1),
            takeUntilDestroyed(this.destroyRef)
          )
          .subscribe({
            next: ({ data }) => {
              this.wheelService.setMarketView(parseFloat(data.balance));
            }
          });
          break;
        }
    }
  }

  private getCachedImage(src: string): HTMLImageElement {
    if (this.imageCache.has(src)) {
      return this.imageCache.get(src)!;
    }

    const img = new Image();
    img.src = src;
    this.imageCache.set(src, img);
    return img;
  }

  private applySegmentTransform(x: number, y: number, angle: number, draw: () => void) {
    this.ctx.save();
    this.ctx.translate(x, y);
    this.ctx.rotate(angle + Math.PI / 2);
    draw();
    this.ctx.restore();
  }

    private drawWheel() {
      const canvas = this.canvasRef!.nativeElement as HTMLCanvasElement;

      // High-DPI support
      const dpr = window.devicePixelRatio || 1;
      const rimWidth = 10;
      const CANVAS_WIDTH = this.canvasSize() === 's' ? 240 : 300;

      // Set actual canvas size
      canvas.width = (CANVAS_WIDTH + 2 * rimWidth) * dpr;
      canvas.height = (CANVAS_WIDTH + 2 * rimWidth) * dpr;

      // Scale back with CSS
      canvas.style.width = `${CANVAS_WIDTH + 2 * rimWidth}px`;
      canvas.style.height = `${CANVAS_WIDTH + 2 * rimWidth}px`;

      this.ctx = canvas.getContext('2d')!;

      // Scale context for high-DPI
      this.ctx.scale(dpr, dpr);

      // Enable smooth rendering
      this.ctx.imageSmoothingEnabled = true;
      this.ctx.imageSmoothingQuality = 'high';

      const center = (CANVAS_WIDTH + 2 * rimWidth) / 2;
      const radius = center - 2 * rimWidth;
      const anglePerSegment = (2 * Math.PI) / this.wheelSegments().length;
      let startAngle = -Math.PI / 2;

      this.applyGrayScaleFilter();

      // Draw the rim
      this.ctx.save();
      this.ctx.lineWidth = rimWidth;
      const outerRimRadius = radius + rimWidth;
      this.ctx.arc(center, center, outerRimRadius, 0, Math.PI * 2);
      this.ctx.fillStyle = '#C74C4D'; // Dark red rim
      this.ctx.fill();
      this.ctx.restore();

      this.wheelSegments().forEach((segment) => {
        const { reward } = segment;
        const midAngle = startAngle + anglePerSegment / 2;

        // Draw segment
      this.ctx.beginPath();
      this.ctx.moveTo(center, center);
      this.ctx.arc(center, center, radius, startAngle, startAngle + anglePerSegment);
      const { x: midArcX, y: midArcY } = this.getMidArcPoint(center, center, radius, startAngle, anglePerSegment);
        const gradient = this.ctx.createLinearGradient(center, center, midArcX, midArcY);

        let segment_color =' ';
        switch (segment.type) {
          case 'bonuses':
            segment_color = 'lightgrey';
            break;
          case 'product':
            segment_color = 'red';
            break
          case 'spins':
            segment_color = '#66A768';
            break;
        }

        let segment_shadow_color = '';
        switch (segment.type) {
          case 'bonuses':
            segment_shadow_color = 'white';
            break;
          case 'product':
            segment_shadow_color = 'brown';
            break;
          case 'spins':
            segment_shadow_color = '#255428';
            break;
        }

      gradient.addColorStop(0, segment_color);
      gradient.addColorStop(1, segment_shadow_color);
      this.ctx.fillStyle = gradient;
      this.ctx.fill();
      this.ctx.lineWidth = 3;
      this.ctx.strokeStyle = 'white';
      this.ctx.stroke();

      const { textPosition: { textX, textY }, imagePosition: { imgX, imgY }} = this.getSegmentTextImagePosition(
        center, center, radius, startAngle, anglePerSegment
        );

        const formatBonusValue = (bonus:string) => {
          const bonusAsNumber = parseInt(bonus);
          return bonusAsNumber ? `+${bonusAsNumber}` : bonusAsNumber.toString();
        }

        let segment_text = null;
        switch (segment.type) {
          case 'bonuses':
            segment_text = formatBonusValue((reward as WheelBonusReward).bonuses);
            break;
          case 'product':
          case 'spins':
            break;
        }

        let segment_img = '';
        switch (segment.type) {
          case 'bonuses':
            segment_img = 'assets/wheel-of-fortune/coins.png';
            break;
          case 'product':
            segment_img = (reward as WheelProductReward).product.category_img;
            break;
          case 'spins':
            segment_img = 'assets/wheel-of-fortune/second-chance.png'
            break;
        }

        const isBonusesType = segment.type === 'bonuses';
        /** Draw text */
        if (segment_text) {
          const textPosX = isBonusesType ? imgX : textX;
          const textPosY = isBonusesType ? imgY : textY;
          this.applySegmentTransform(textPosX, textPosY, midAngle, () => {
          this.ctx.fillStyle = 'black';
          this.ctx.font = '14px Arial';
          this.ctx.textAlign = 'center';
          this.ctx.fillText(segment_text, 0, isBonusesType? -4 : 40);
        });
      }

      /** Draw image */
      if (segment_img) {
        const img = this.getCachedImage(segment_img);

        const imagePosX = isBonusesType ? textX : imgX;
        const imagePosY = isBonusesType ? textY : imgY;

        this.applySegmentTransform(imagePosX, imagePosY, midAngle, () => {
          const maxSize = isBonusesType? 28 : 40;

          // Calculate scaling factor to fit within maxSize while preserving aspect ratio
          const scale = Math.min(maxSize / img.width, maxSize / img.height);

          // Calculate actual dimensions
          const scaledWidth = img.width * scale;
          const scaledHeight = img.height * scale;

          const imgY = -scaledHeight / 2;

          // Draw image centered with preserved aspect ratio
          this.ctx.drawImage(
            img,
            -scaledWidth / 2,
            //adjust bonus img y
            isBonusesType ? imgY-10 : imgY,
            scaledWidth,
            scaledHeight
          );
        });
      }
        startAngle += anglePerSegment;
      });


    /** Draw the central piece. Move to the end of a method to avoid overwrites */
      this.drawCentralPiece(center);
  }

  private getMidArcPoint(centerX: number, centerY: number, radius: number, startAngle: number, anglePerSegment: number) {
    const midAngle = startAngle + anglePerSegment / 2;

    const midX = centerX + Math.cos(midAngle) * radius;
    const midY = centerY + Math.sin(midAngle) * radius;

    return { x: midX, y: midY };
  }

  private getSegmentTextImagePosition(
    centerX: number,
    centerY: number,
    radius: number,
    startAngle: number,
    anglePerSegment: number,
) {

  const textRadius = radius * 0.5;
  const imageRadius = radius * 0.8;

  const { x: textX, y: textY } = this.getMidArcPoint(centerX, centerY, textRadius, startAngle, anglePerSegment);
  const { x: imgX, y: imgY } = this.getMidArcPoint(centerX, centerY, imageRadius, startAngle, anglePerSegment);
    return { textPosition: { textX, textY }, imagePosition: { imgX, imgY } };
}

  private drawCentralPiece(center: number) {
      this.ctx.beginPath();
      this.ctx.arc(center, center, 15, 0, Math.PI * 2);
      this.ctx.fillStyle = 'white';
      this.ctx.fill();
      this.ctx.closePath();
  }

  private applyGrayScaleFilter():void {
    !this.canSpin() && (this.ctx.filter = 'grayscale(.6)');
  }

  private spinWheel() {
    this.startContinuousSpin();
    this.wheelService.requestWheelSpinResult()
      .pipe(
        tap(({ data }) => {
          if (data && data.reward) {
            const segmentIndex = this.wheelSegments().findIndex(s => s.position == data.reward.position);

            if (segmentIndex === -1) {
              console.error('Winning segment not found!');
              return;
            }

            // Calculate the angle needed to position this segment at the top
            const segmentAngle = (360 / this.wheelSegments().length);
            const segmentCenterAngle = segmentIndex * segmentAngle + (segmentAngle / 2);

            let rotationToTarget = (360 - segmentCenterAngle) % 360;

            // Always ensure minimum rotations for visual effect
            const minSpins = 6; // minimum 6 full rotations
            const maxSpins = 10; // maximum 10 full rotations

            // Add random extra spins within our range
            const extraSpins = minSpins + Math.floor(Math.random() * (maxSpins - minSpins + 1));
            const targetAngle = (extraSpins * 360) + rotationToTarget;

            this.stopContinuousSpin(targetAngle);
          }
        }),
      ).subscribe({
        error: (error: HttpErrorResponse) => {
          const isError = true;
          this.stopContinuousSpin(360, isError);
          this.errorMessage.set(error.error.message);
          this.showErrorTooast();
          this.isLoading.set(false);
        },
      });
  }

  private stopContinuousSpin(targetRotation: number, isError = false) {
    this.isSpinning = false;

    const start = performance.now();
    const duration = 4500;
      const initialAngle = this.currentAngle;


    const animate = (time: number) => {
      const elapsed = time - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 4); // cubic easing out

      this.currentAngle = initialAngle + eased * (targetRotation - initialAngle);
      this.renderWheel();
      this.ctx.canvas.style.transform = `rotate(${this.currentAngle.toFixed(2)}deg)`;

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        timer(1000).pipe(take(1)).subscribe(() => {
          if (!isError) {
            this.showTrophy.set(true);
            this.wheelService.setAfterSpinView();
            this.isLoading.set(false);
          }
        });
      }
    };

    requestAnimationFrame(animate);
  }


  private startContinuousSpin(defaultVelocity?:number) {
  this.isSpinning = true;
  let lastTime = performance.now();
  let velocity = defaultVelocity || 20; // Fast initial speed

  const spinLoop = (time: number) => {
    if (!this.isSpinning || this.showSpinMarket()) return; // Stop loop when response arrives

    const deltaTime = time - lastTime;
    this.currentAngle += velocity * (deltaTime / 16); // Scale velocity based on frame time

    this.renderWheel();
    this.ctx.canvas.style.transform = `rotate(${this.currentAngle.toFixed(2)}deg)`;

    lastTime = time;
    requestAnimationFrame(spinLoop);
  };

  requestAnimationFrame(spinLoop);
  }

  private renderWheel() {
  const canvas = this.canvasRef.nativeElement as HTMLCanvasElement;
  const ctx = this.ctx;
  const center = canvas.width / 2;

  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.save();
  ctx.translate(center, center);
  ctx.rotate((this.currentAngle * Math.PI) / 180);
  ctx.translate(-center, -center);
  this.drawWheel();
  ctx.restore();
}

  public handleBuySpins(event:WheelBonus): void {
    this.wheelApiService.buyWheelSpins({ spins: event.spins })
      .pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef)
    )
      .subscribe({
        next: () => {
          this.wheelService.handleWheelAction(WheelAction.NAVIGATE_TO_WHEEL);
          this.initDefaultWheel();
        },
        error: (error: HttpErrorResponse) => {
          this.errorMessage.set(error.error.message);
          this.showErrorTooast();
        }
    })
  }

  private showErrorTooast(): void {
    this.toastService.show({
      header: 'Error',
      body: '',
      classname: 'bg-danger text-light',
      autohide: true,
      template: this.errorRef
    })
  }
}
